import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { API_URL } from "../lib/api";

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase() || "?";
}

export function ProfileAvatar({ userId, schoolId, name, className, fallback, fallbackStyle, hasPhoto, deferUntilVisible = false }: {
  userId?: string | null;
  schoolId: string;
  name: string;
  className: string;
  fallback?: ReactNode;
  fallbackStyle?: CSSProperties;
  hasPhoto?: boolean;
  deferUntilVisible?: boolean;
}) {
  const [photoUrl, setPhotoUrl] = useState("");
  const [visible, setVisible] = useState(!deferUntilVisible);
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (hasPhoto === false || !deferUntilVisible || visible) return;
    const element = rootRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setVisible(true);
      observer.disconnect();
    }, { rootMargin: "80px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [deferUntilVisible, hasPhoto, visible]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    const controller = new AbortController();
    setPhotoUrl("");
    if (!userId || !schoolId || hasPhoto === false || !visible) return () => {
      active = false;
      controller.abort();
    };

    void fetch(`${API_URL}/api/users/${encodeURIComponent(userId)}/profile-photo?schoolId=${encodeURIComponent(schoolId)}`, {
      credentials: "include",
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return;
      objectUrl = URL.createObjectURL(await response.blob());
      if (active) setPhotoUrl(objectUrl);
      else URL.revokeObjectURL(objectUrl);
    }).catch(() => undefined);

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, schoolId, hasPhoto, visible]);

  return (
    <span ref={rootRef} className={className} aria-label={name} style={!photoUrl ? fallbackStyle : undefined}>
      {photoUrl ? <img src={photoUrl} alt="" /> : (fallback ?? initials(name))}
    </span>
  );
}
